import { useQuery } from '@tanstack/react-query';
import { directory, location } from '@cwos/api-client';
import { Pill, SkeletonSet } from '@cwos/ui-admin';
import { useAuth } from '../../lib/auth.js';
import { useNav } from '../navigation.js';
import { SettingsCard, SettingsHeader } from './common.js';
import { LoadFailure } from '../LoadFailure.js';

const MODE_CONSEQUENCE: Record<string, string> = {
  off: 'Location isn’t collected on your shifts.',
  record_only: 'Your location is noted during shifts — it’s never used to confirm where you were.',
  warn_outside: 'You’ll see a heads-up if you clock in away from a work site; it never blocks you.',
  require_geofence:
    'Clocking in away from a work site asks you to attest — an attested punch is never shown as verified.',
};

/**
 * Your profile.
 *
 * Everything here comes from the authenticated principal (`/auth/me`) plus, for
 * people who are also workers, their own directory record. Two rules shape it:
 *
 *  1. IDENTITY IS NOT OURS TO EDIT. Name and email come from the identity
 *     provider that authenticated you. Phibian does not own them, so it does not
 *     render them in a text box that pretends a change here would stick. The
 *     page says where they come from instead.
 *
 *  2. NOTHING IS FAKED FOR NON-WORKERS. An administrator is not necessarily a
 *     worker; when there is no worker record, the employment card is absent
 *     rather than filled with dashes.
 *
 * This is a per-user screen driven entirely by the signed-in principal, so it
 * behaves the same in every organization.
 */
export function MeProfile() {
  const { me, logout } = useAuth();
  const navigate = useNav();

  const policy = useQuery({
    queryKey: ['location', 'policy', 'effective', 'me'],
    queryFn: () => location.effectivePolicy(),
  });

  // A principal may or may not also be a worker. A 403/404 here is an ordinary
  // answer ("you are not a worker"), not an error worth showing.
  const worker = useQuery({
    queryKey: ['directory', 'workers', 'me'],
    queryFn: () => directory.me(),
    retry: false,
  });

  const displayName = me?.name?.trim() || me?.email?.trim() || '—';
  const workerRecord = worker.data ?? null;
  const membershipCount = me?.memberships?.length ?? 0;

  return (
    <div className="rn-col" style={{ maxWidth: 760 }}>
      <SettingsHeader
        title="Your profile"
        blurb="Who Phibian knows you as, what you can do, and how your organization handles your location."
      />

      <SettingsCard>
        <h2 className="rn-card-title">Who you are</h2>
        <dl
          style={{
            display: 'grid',
            gridTemplateColumns: 'max-content 1fr',
            gap: 'var(--space-8) var(--space-16)',
            margin: 'var(--space-12) 0 0',
          }}
        >
          <dt className="rn-muted">Name</dt>
          <dd style={{ margin: 0 }}>{displayName}</dd>
          <dt className="rn-muted">Email</dt>
          <dd style={{ margin: 0 }}>{me?.email?.trim() || '—'}</dd>
          <dt className="rn-muted">Organization</dt>
          <dd style={{ margin: 0 }}>{me?.orgName ?? '—'}</dd>
          <dt className="rn-muted">Sign-in strength</dt>
          <dd style={{ margin: 0 }}>
            {me?.authStrength === 'mfa' ? (
              <Pill variant="good">Multi-factor</Pill>
            ) : (
              <Pill variant="plain">Standard</Pill>
            )}
          </dd>
        </dl>
        <p className="rn-muted" style={{ marginTop: 'var(--space-14)', marginBottom: 0 }}>
          Your name and email come from the identity provider you sign in with, so they’re changed
          there rather than here. Ask an administrator in your organization if they need updating.
        </p>
      </SettingsCard>

      <SettingsCard>
        <h2 className="rn-card-title">What you can do</h2>
        <p className="rn-muted" style={{ marginTop: 'var(--space-6)' }}>
          Access is granted deliberately — nothing is enabled by default.
        </p>
        <dl
          style={{
            display: 'grid',
            gridTemplateColumns: 'max-content 1fr',
            gap: 'var(--space-8) var(--space-16)',
            margin: 'var(--space-12) 0 0',
          }}
        >
          <dt className="rn-muted">Roles</dt>
          <dd style={{ margin: 0 }}>
            {me?.roles?.length ? (
              <span style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-6)' }}>
                {me.roles.map((r) => (
                  <Pill key={r} variant="plain">
                    {r}
                  </Pill>
                ))}
              </span>
            ) : (
              '—'
            )}
          </dd>
          <dt className="rn-muted">Organizations</dt>
          <dd style={{ margin: 0 }}>
            {membershipCount > 1
              ? `${membershipCount} — switch between them from the account menu`
              : me?.orgName
                ? '1'
                : '—'}
          </dd>
        </dl>
      </SettingsCard>

      {workerRecord ? (
        <SettingsCard>
          <h2 className="rn-card-title">Your work record</h2>
          <p className="rn-muted" style={{ marginTop: 'var(--space-6)' }}>
            Held by your organization. Ask your supervisor if something here is wrong — corrections
            are recorded, and the original is kept.
          </p>
          <dl
            style={{
              display: 'grid',
              gridTemplateColumns: 'max-content 1fr',
              gap: 'var(--space-8) var(--space-16)',
              margin: 'var(--space-12) 0 0',
            }}
          >
            <dt className="rn-muted">Display name</dt>
            <dd style={{ margin: 0 }}>{workerRecord.displayName}</dd>
            {workerRecord.roleTitle ? (
              <>
                <dt className="rn-muted">Role title</dt>
                <dd style={{ margin: 0 }}>{workerRecord.roleTitle}</dd>
              </>
            ) : null}
            {workerRecord.expectedWeeklyHours != null ? (
              <>
                <dt className="rn-muted">Expected weekly hours</dt>
                <dd style={{ margin: 0 }}>{workerRecord.expectedWeeklyHours}h</dd>
              </>
            ) : null}
            <dt className="rn-muted">Employment status</dt>
            <dd style={{ margin: 0 }}>{workerRecord.employmentStatus}</dd>
          </dl>
        </SettingsCard>
      ) : null}

      <SettingsCard>
        <h2 className="rn-card-title">Your location policy</h2>
        {policy.isLoading ? (
          <SkeletonSet surface="table" rows={2} />
        ) : policy.isError && !policy.data ? (
          <LoadFailure
            error={policy.error}
            message="We couldn’t load your policy just now."
            onRetry={() => void policy.refetch()}
          />
        ) : policy.data ? (
          <p style={{ marginBottom: 0, marginTop: 'var(--space-8)' }}>
            {MODE_CONSEQUENCE[policy.data.mode] ??
              'Your location policy is set by your organization.'}
          </p>
        ) : null}
      </SettingsCard>

      {/*
        THE WAY IN TO A PERSON'S OWN MEMORY RECORD.

        `GET /api/intelligence/memory/about-me` has existed since migration
        1842000000000 and nothing in either app called it, so "see and control
        what Phibian remembers about you" was reachable only by curl. It is
        linked from here rather than from the Settings centre because it is the
        person's own record and not an organizational control, and because this
        screen is in the account menu for every principal — including one who
        holds no settings permission and would never see a Settings entry.
      */}
      <SettingsCard>
        <h2 className="rn-card-title">What Phibian remembers about you</h2>
        <p className="rn-muted" style={{ marginTop: 'var(--space-6)' }}>
          Everything Phibian has recorded about you personally, where each record came from, whether
          it is still shaping the answers you get, and how to stop it.
        </p>
        <button
          type="button"
          className="rn-btn-quiet"
          style={{ marginTop: 'var(--space-12)' }}
          onClick={() => navigate('/me/memory')}
        >
          See what Phibian remembers
        </button>
      </SettingsCard>

      <div style={{ display: 'flex', gap: 'var(--space-12)', flexWrap: 'wrap' }}>
        <button type="button" className="rn-btn-quiet" onClick={() => navigate('/preview/worker')}>
          View as worker
        </button>
        <button
          type="button"
          className="signin-devbtn"
          style={{ width: 'auto', padding: 'var(--space-10) var(--space-16)' }}
          onClick={() => void logout()}
        >
          Sign out
        </button>
      </div>
    </div>
  );
}
